import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import LoaderOverlay from '../../Components/shared/Loader';
import AlertService from '../../services/utility/alertService';
import { getMasterRequestResources, saveMasterRequest } from '../../services/api/masterRequestService';
import DynamicFields from './DynamicFields';
import './MasterRequest.css';
import {
    REQUEST_TYPES,
    REQUEST_TYPE_OPTIONS,
    getFieldList,
    getInitialValues,
    buildValidationSchema
} from './masterRequestConfig';

const NewMasterRequest = () => {
    const [reqType, setReqType] = useState(REQUEST_TYPES.ITEM.value);
    const [loading, setLoading] = useState(true);
    const [options, setOptions] = useState({ customers: [] });
    const navigate = useNavigate();

    useEffect(() => {
        const loadMasterData = async () => {
            try {
                const response = await getMasterRequestResources();
                const customers = (response.data?.customers || []).map(c => ({
                    value: c.CusCode,
                    label: `${c.CusCode} - ${c.CusName}`
                }));
                setOptions({ customers });
            } catch (error) {
                console.error('Error loading master data:', error);
                AlertService.errorAlert('Unable to load customer list. Please refresh and try again.');
            } finally {
                setLoading(false);
            }
        };

        loadMasterData();
    }, []);

    // Only the fields of the selected type are in the form state, so the payload can
    // never carry a value belonging to the other request type.
    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        const request = getFieldList(reqType).reduce((payload, field) => {
            payload[field.name] = (values[field.name] || '').trim();
            return payload;
        }, { REQ_TYPE: reqType });

        setLoading(true);

        try {
            const response = await saveMasterRequest(request);
            const newId = response.data?.ID;

            resetForm({ values: getInitialValues(reqType) });
            // The loader overlay is position:fixed with a z-index above SweetAlert2's,
            // so it must come down BEFORE the modal is awaited - otherwise it sits on
            // top of the modal, blocks the OK button, the await never resolves, and
            // the loader is stuck on screen forever.
            setLoading(false);
            await AlertService.successAlert(
                newId
                    ? `Request #${newId} submitted successfully.`
                    : 'Request submitted successfully.'
            );
            navigate('/MyMasterRequest');
        } catch (error) {
            console.error('Error submitting request:', error);
            const message = typeof error.response?.data === 'string'
                ? error.response.data
                : error.response?.data?.Message || 'Error submitting request. Please try again.';
            setLoading(false);
            AlertService.errorAlert(message);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    return (
        <div className="card aon-card master-request-page">
            <LoaderOverlay loading={loading} message="Loading" height="500px" />
            <div className="card-header aon-card-header">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h4 className="mb-0">New Customer/Item Request</h4>
                    <Button variant="outline-primary" onClick={() => navigate('/MyMasterRequest')}>
                        My Requests
                    </Button>
                </div>
            </div>
            {/* No nested Container/Card here - on a 390px phone the theme's own
                padding plus a container plus an inner card left roughly 270px for
                the fields. Sitting straight in the card body gives back ~58px. */}
            <div className="card-body aon-card-body">
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Select Request Type</Form.Label>
                            <Select
                                inputId="reqType"
                                options={REQUEST_TYPE_OPTIONS}
                                value={REQUEST_TYPE_OPTIONS.find(t => t.value === reqType)}
                                onChange={(option) => setReqType(option.value)}
                                isSearchable={false}
                                className="react-select-container"
                                classNamePrefix="react-select"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="d-flex align-items-end">
                        <Alert variant="info" className="mb-0 mt-3 mt-md-0 w-100 py-2 small">
                            The data admin creates the record and updates this request.
                        </Alert>
                    </Col>
                </Row>

                <hr className="my-3" />

                <Formik
                    key={reqType}
                    initialValues={getInitialValues(reqType)}
                    validationSchema={buildValidationSchema(reqType)}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleSubmit, isSubmitting, setFieldValue, setFieldTouched, resetForm }) => (
                        <Form onSubmit={handleSubmit} noValidate>
                            <DynamicFields
                                fields={getFieldList(reqType)}
                                values={values}
                                errors={errors}
                                touched={touched}
                                options={options}
                                setFieldValue={setFieldValue}
                                setFieldTouched={setFieldTouched}
                            />

                            {/* Stacked full-width on a phone, right-aligned from md up.
                                DOM order puts Submit last, so it is the bottom button
                                on a phone and the rightmost one on desktop. */}
                            <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-2">
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    size="lg"
                                    disabled={isSubmitting}
                                    onClick={() => resetForm({ values: getInitialValues(reqType) })}
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    size="lg"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default NewMasterRequest;
